import { useEffect, useRef, useState } from "react";
import { apiPost } from "../lib/apiClient.js";

/**
 * Claim a link-shared forecast the viewer arrived at by URL.
 *
 * WHY THIS EXISTS. A link share is one grant, `link:any`, held by every signed-in
 * viewer — so it deliberately does NOT enumerate (every link-shared forecast
 * would otherwise appear in every user's Recent, and the URL would stop being
 * what narrows the audience). The consequence is that a recipient opening the URL
 * has a forecast id their lists do not contain. `POST /:id/claim` is the receive
 * event that fixes that: it records a grant naming them, so the forecast appears
 * in their manager — at the top of Recent, because the claim's timestamp is the
 * received-at the list sorts by.
 *
 * FIRES ONLY ON A GENUINE MISS. `enabled` is the caller's "the URL names a
 * forecast and both lanes have settled without finding it" — claiming for a
 * forecast already in hand would be a pointless write, and claiming before the
 * lanes settle would fire for every viewer on every boot.
 *
 * ONE ATTEMPT PER ID, ever. A 404 (not link-shared, or no such forecast) is the
 * common negative and must not retry-loop; a network failure is treated the same
 * way, because the honest fallback is the same — the deep link opens a blank home
 * rather than hanging. The id is keyed so navigating to a different link in the
 * same session can still claim.
 *
 * @param {object} args
 * @param {string|null|undefined} args.forecastId the id from `?forecast=`
 * @param {boolean} args.enabled the caller's miss condition (see above)
 * @param {() => (void|Promise<unknown>)} [args.onClaimed] run after a successful
 *   claim — the caller reloads the shared lane so the new row lands in the store
 * @param {typeof apiPost} [args.apiPostImpl] injectable for tests
 * @returns {{claimed: boolean, refused: boolean}} `claimed` is true once a claim has succeeded
 *   for the current id — the caller's signal that there is now a row to look for; `refused` is
 *   true once it has failed (a refusal or a network error), so the caller can say the link could
 *   not be opened instead of waiting for a row that will not come (SOO-862)
 */
export function useLinkClaim({ forecastId, enabled, onClaimed, apiPostImpl = apiPost }) {
  // Only `claimed`, and it is set AFTER the request resolves. An earlier version
  // also exposed `claiming`, set synchronously at the top of the effect — which
  // the React compiler rejects (a synchronous setState in an effect body triggers
  // cascading renders) and which no caller needed: the signal that matters is
  // "there is now a row to look for", not "a request is open".
  const [claimed, setClaimed] = useState(false);
  const [refused, setRefused] = useState(false);
  // Which ids this session has already tried. A Set, not a boolean: a session can
  // legitimately visit two different links.
  const triedRef = useRef(new Set());
  // UNMOUNT ONLY — its own effect with an empty dep array, the `aliveRef` pattern
  // useForecastShares.js carries for the same reason. The claim effect's cleanup
  // also fires on every dep change, which is a different question: a re-render
  // mid-claim would otherwise discard a grant the server has already written, and
  // `triedRef` makes that permanent by refusing the retry.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!enabled || !forecastId) return;
    if (triedRef.current.has(forecastId)) return;
    triedRef.current.add(forecastId);
    (async () => {
      let ok = false;
      try {
        // telemetry-no-intent: app-initiated on arrival from a forecast link; forecast.open (link) records it
        const res = await apiPostImpl(`/api/forecasts/${encodeURIComponent(forecastId)}/claim`, {});
        ok = res?.ok === true;
      } catch {
        // Same outcome as a 404: no claim, no retry. The deep link falls through
        // to a blank home, which is the honest rendering of "you cannot see this".
        ok = false;
      }
      if (!aliveRef.current) return;
      if (!ok) {
        setRefused(true);
        return;
      }
      // HYDRATE FIRST, THEN SIGNAL. `claimed` means "there is now a row to look
      // for", so it must not lead the reload that puts the row there — a caller
      // reacting to it would resolve against a store the row has not reached.
      try {
        await onClaimed?.();
      } catch {
        // The CLAIM succeeded; only the reload failed. Still signal, or a caller
        // waiting on this flag waits forever.
      }
      if (!aliveRef.current) return;
      setClaimed(true);
    })();
    // `onClaimed` is a dep rather than being held in a ref: writing a ref during
    // render is what the React compiler forbids, and a ref is not needed here
    // anyway — `triedRef` already makes a re-fire a no-op, so a caller passing an
    // inline arrow costs an effect re-run that returns immediately.
  }, [forecastId, enabled, apiPostImpl, onClaimed]);

  return { claimed, refused };
}
